/**
 * The box a form control is drawn in, and the three states it moves through.
 *
 * Input and Textarea sit next to each other on the same form, so a tinted box
 * beside a transparent one reads as a bug rather than as a distinction. Written
 * out in each file they matched by luck; here they match by construction, and
 * the next control added to the set gets them for free rather than copying them
 * a third time.
 *
 * The two halves are separate because a component needs to slot its own sizing
 * between them — `h-9` for a single line, `field-sizing-content` for one that
 * grows. They share their states, not their shape.
 */

/**
 * At rest. White from the start (`bg-surface`), so an empty field reads as a
 * place to write rather than a greyed-out box; the border and, on focus, the
 * mint ring are what mark the one being typed in. Still a fill rather than
 * shadcn's `bg-transparent`, so a field holds its white on the off-white page
 * and on a tinted card alike. Disabled keeps the grey, which is now what sets
 * a locked field apart.
 *
 * No vertical padding: the two controls set their own, and a value here would
 * only be overridden anyway.
 *
 * Typed text is 16px below `md` because iOS zooms the page into any field set
 * smaller. That rule is about what is typed, so the placeholder stays at the
 * control size (13px) on every screen — the zoom keys off the field's own
 * font-size, not its placeholder's.
 */
export const controlBase =
  'flex w-full rounded-input border border-input bg-surface px-3 text-base shadow-xs transition-[color,background-color,box-shadow] outline-none placeholder:text-sm placeholder:text-ink-muted md:text-sm';

/**
 * Focus, invalid, disabled.
 *
 * The focus ring is the bright mint, not the dark one the border uses: a glow
 * has to read against the page at a glance, and mint-700 at 50% on the off-white
 * ground barely registers.
 *
 * The invalid border is one deliberate departure from the mockup, whose
 * `#d06a6a` is 2.7:1 against its own fill — under the 3:1 WCAG asks of a
 * non-text indicator, so an error nobody can see. This keeps shadcn's stronger
 * `destructive` border at 6.5:1 and puts the mockup's wash behind it.
 *
 * Disabled names its colours rather than reaching for `opacity-50`: dimming a
 * filled box washes the fill out along with the text, and the result reads as a
 * rendering fault rather than as a locked field.
 */
export const controlStates = [
  'focus-visible:border-ring focus-visible:bg-surface focus-visible:ring-3 focus-visible:ring-brand/50',
  'aria-invalid:border-destructive aria-invalid:bg-danger-wash aria-invalid:ring-destructive/20',
  'disabled:cursor-not-allowed disabled:border-hairline disabled:bg-raised disabled:text-ink-disabled disabled:placeholder:text-ink-disabled',
].join(' ');
